import React, { useState, useMemo } from 'react';
import { Select } from 'antd';
import './index.less';

interface CustomPaginationProps {
  total: number;
  current?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onChange?: (page: number, pageSize: number) => void;
  onShowSizeChange?: (current: number, size: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  total,
  current = 1,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  onChange,
  onShowSizeChange,
}) => {
  const [jumpValue, setJumpValue] = useState<string>('');

  // 计算总页数
  const totalPages = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  // 生成页码数组
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // 最多显示7个页码

    if (totalPages <= maxVisible) {
      // 总页数少于等于7，全部显示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 总页数大于7，需要省略
      if (current <= 4) {
        // 当前页在前面
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (current >= totalPages - 3) {
        // 当前页在后面
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 当前页在中间
        pages.push(1);
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }, [current, totalPages]);

  // 处理页码变化
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === current) return;
    onChange?.(page, pageSize);
  };

  // 处理每页条数变化
  const handlePageSizeChange = (value: number) => {
    onShowSizeChange?.(1, value);
    onChange?.(1, value);
  };

  // 处理跳转
  const handleJump = () => {
    const page = Number(jumpValue);
    if (page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setJumpValue('');
    }
  };

  // 处理回车键跳转
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleJump();
    }
  };

  return (
    <div className="custom-pagination-wrapper">
      {/* 1. 总条数 */}
      <div className="pagination-total">
        共 {total} 条数据
      </div>

      {/* 2. 每页条数选择器 */}
      <div className="pagination-size-changer">
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          options={pageSizeOptions.map(size => ({
            label: `${size} 条/页`,
            value: size,
          }))}
          style={{ width: 110 }}
        />
      </div>

      {/* 3. 上一页 */}
      {current > 1 && (
        <button
          className="pagination-btn pagination-prev"
          onClick={() => handlePageChange(current - 1)}
        >
          <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
            <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
          </svg>
        </button>
      )}

      {/* 4. 页码 */}
      <div className="pagination-pages">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                •••
              </span>
            );
          }
          return (
            <button
              key={page}
              className={`pagination-page ${page === current ? 'active' : ''}`}
              onClick={() => handlePageChange(page as number)}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* 5. 下一页 */}
      {current < totalPages && (
        <button
          className="pagination-btn pagination-next"
          onClick={() => handlePageChange(current + 1)}
        >
          <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
            <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z" />
          </svg>
        </button>
      )}

      {/* 6. 快速跳转 */}
      <div className="pagination-jumper">
        <span>跳至</span>
        <input
          type="number"
          min="1"
          max={totalPages}
          value={jumpValue}
          onChange={(e) => setJumpValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder=""
        />
        <span>页</span>
      </div>
    </div>
  );
};

export default CustomPagination;
